export interface CsvParseError {
  line: number
  message: string
}

export interface CsvParseResult {
  headers: string[]
  rows: Record<string, string>[]
  errors: CsvParseError[]
}

const splitCsvLine = (line: string, sep: string): string[] => {
  const out: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        cur += c
      }
    } else {
      if (c === '"') {
        inQuotes = true
      } else if (c === sep) {
        out.push(cur)
        cur = ''
      } else {
        cur += c
      }
    }
  }
  out.push(cur)
  return out
}

const detectSeparator = (firstLine: string): string => {
  const commas = (firstLine.match(/,/g) ?? []).length
  const semis = (firstLine.match(/;/g) ?? []).length
  return semis > commas ? ';' : ','
}

export const parseCsv = (
  text: string,
  expectedHeaders: string[],
): CsvParseResult => {
  const errors: CsvParseError[] = []
  const stripped = text.replace(/^﻿/, '')
  const rawLines = stripped.split(/\r?\n/).filter((l) => l.trim() !== '')

  if (rawLines.length === 0) {
    return { headers: [], rows: [], errors: [{ line: 0, message: 'Fichier vide' }] }
  }

  const sep = detectSeparator(rawLines[0])
  const headers = splitCsvLine(rawLines[0], sep).map((h) => h.trim())

  const missing = expectedHeaders.filter((h) => !headers.includes(h))
  if (missing.length > 0) {
    errors.push({
      line: 1,
      message: `Colonnes manquantes: ${missing.join(', ')}`,
    })
  }

  const rows: Record<string, string>[] = []
  for (let i = 1; i < rawLines.length; i++) {
    const cells = splitCsvLine(rawLines[i], sep).map((v) => v.trim())
    const row: Record<string, string> = {}
    headers.forEach((h, idx) => {
      row[h] = cells[idx] ?? ''
    })
    rows.push(row)
  }

  return { headers, rows, errors }
}

const escapeCell = (v: string): string => {
  if (v.includes('"') || v.includes(',') || v.includes('\n')) {
    return '"' + v.replace(/"/g, '""') + '"'
  }
  return v
}

export const buildCsvTemplate = (
  headers: string[],
  sampleRows: string[][] = [],
): string => {
  const lines = [headers.map(escapeCell).join(',')]
  for (const r of sampleRows) {
    lines.push(r.map(escapeCell).join(','))
  }
  return lines.join('\r\n') + '\r\n'
}

export const downloadCsv = (filename: string, content: string): void => {
  const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
